import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { Globe, Lock, Users, Edit, Trash2, Share2, Plus, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Group {
  id: string;
  name: string;
  description: string;
  avatar_url: string;
  cover_image: string;
  visibility: 'public' | 'private';
  requires_approval: boolean;
  max_members: number | null;
  creator: {
    id: string;
    full_name: string;
    avatar_url: string;
  };
  members: Array<{
    user: {
      id: string;
      full_name: string;
      avatar_url: string;
    };
    role: 'owner' | 'admin' | 'moderator' | 'member';
    joined_at: string;
  }>;
  posts: Array<{
    id: string;
    content: string;
    media_url: string | null;
    media_type: 'image' | 'video' | null;
    created_at: string;
    user: {
      id: string;
      full_name: string;
      avatar_url: string;
    };
  }>;
  user_member?: {
    role: 'owner' | 'admin' | 'moderator' | 'member';
  }[];
}

export function GroupDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState<Group | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [activeTab, setActiveTab] = useState<'posts' | 'members'>('posts');
  const [newPost, setNewPost] = useState('');
  const [postLoading, setPostLoading] = useState(false);

  useEffect(() => {
    loadGroup();
  }, [id]);

  const loadGroup = async () => {
    try {
      const { data, error } = await supabase
        .from('groups')
        .select(`
          *,
          creator:profiles!creator_id(*),
          members:group_members!inner(
            user:profiles!group_members_user_id_fkey(*),
            role,
            joined_at
          ),
          posts:group_posts(
            *,
            user:profiles(*)
          ),
          user_member:group_members!group_members_group_id_fkey(role)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setGroup(data);
    } catch (error) {
      console.error('Error loading group:', error);
      setError('Failed to load group');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!group) return;
    setJoining(true);

    try {
      const { error } = await supabase.rpc('join_group', {
        group_uuid: group.id
      });

      if (error) throw error;
      await loadGroup();
    } catch (error) {
      console.error('Error joining group:', error);
    } finally {
      setJoining(false);
    }
  };

  const handleDelete = async () => {
    if (!group || !window.confirm('Are you sure you want to delete this group?')) return;

    try {
      const { error } = await supabase
        .from('groups')
        .delete()
        .eq('id', group.id);

      if (error) throw error;
      navigate('/groups');
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };

  const handleCreatePost = async () => {
    if (!group || !user || !newPost.trim() || postLoading) return;
    
    setPostLoading(true);
    try {
      const { error: postError } = await supabase
        .from('group_posts')
        .insert({
          group_id: group.id,
          user_id: user.id,
          content: newPost.trim()
        });

      if (postError) throw postError;

      setNewPost('');
      await loadGroup();
    } catch (error) {
      console.error('Error creating post:', error);
    } finally {
      setPostLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-royal-purple"></div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 dark:text-red-400">{error || 'Group not found'}</p>
      </div>
    );
  }

  const isOwner = group.user_member?.[0]?.role === 'owner';
  const isAdmin = group.user_member?.[0]?.role === 'admin';
  const isModerator = group.user_member?.[0]?.role === 'moderator';
  const isMember = group.user_member?.length > 0;

  const membersByRole = {
    owner: group.members.filter(m => m.role === 'owner'),
    admin: group.members.filter(m => m.role === 'admin'),
    moderator: group.members.filter(m => m.role === 'moderator'),
    member: group.members.filter(m => m.role === 'member')
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Cover image */}
      <div className="relative h-64 rounded-xl overflow-hidden">
        <img
          src={group.cover_image || 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?auto=format&fit=crop&q=80'}
          alt={group.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      {/* Group header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <img
            src={group.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(group.name)}`}
            alt={group.name}
            className="w-24 h-24 rounded-full ring-4 ring-white dark:ring-dark-paper -mt-12 relative"
          />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{group.name}</h1>
            <div className="mt-2 flex items-center gap-4 text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-2">
                {group.visibility === 'public' ? (
                  <Globe className="w-4 h-4" />
                ) : (
                  <Lock className="w-4 h-4" />
                )}
                <span className="capitalize">{group.visibility} group</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>{group.members.length} members</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isOwner && (
            <>
              <button
                onClick={() => navigate(`/groups/${group.id}/edit`)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-elevated rounded-lg transition-colors"
              >
                <Edit className="w-5 h-5" />
              </button>
              <button
                onClick={handleDelete}
                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </>
          )}
          <button
            onClick={() => {/* Implement share */}}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-elevated rounded-lg transition-colors"
          >
            <Share2 className="w-5 h-5" />
          </button>
          {!isMember && (
            <button
              onClick={handleJoin}
              disabled={joining}
              className="px-4 py-2 bg-royal-purple text-white rounded-lg hover:bg-royal-purple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {joining ? 'Joining...' : 'Join Group'}
            </button>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="prose dark:prose-invert max-w-none">
        <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">{group.description}</p>
      </div>

      {/* Tabs */}
      <div className="border-b dark:border-dark-border">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('posts')}
            className={cn(
              "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap",
              activeTab === 'posts'
                ? "border-royal-purple text-royal-purple"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            )}
          >
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              <span>Posts</span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('members')}
            className={cn(
              "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap",
              activeTab === 'members'
                ? "border-royal-purple text-royal-purple"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            )}
          >
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              <span>Members</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Tab content */}
      <div className="min-h-[400px]">
        {activeTab === 'posts' && (
          <div className="space-y-6">
            {/* Create post form */}
            {isMember && (
              <div className="bg-white dark:bg-dark-paper rounded-xl shadow-sm p-4">
                <div className="flex gap-4">
                  <img
                    src={user?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(user?.email || '')}`}
                    alt="Your avatar"
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="flex-1">
                    <textarea
                      value={newPost}
                      onChange={(e) => setNewPost(e.target.value)}
                      placeholder="Write something..."
                      className="w-full px-3 py-2 border dark:border-dark-border rounded-lg resize-none focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:text-gray-100"
                      rows={3}
                    />
                    <div className="mt-2 flex justify-end">
                      <button
                        onClick={handleCreatePost}
                        disabled={!newPost.trim() || postLoading}
                        className={cn(
                          "px-4 py-2 rounded-lg font-medium transition-colors",
                          !newPost.trim() || postLoading
                            ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                            : "bg-royal-purple text-white hover:bg-royal-purple/90"
                        )}
                      >
                        {postLoading ? 'Posting...' : 'Post'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Posts list */}
            {group.posts.length > 0 ? (
              <div className="space-y-6">
                {group.posts.map((post) => (
                  <div
                    key={post.id}
                    className="bg-white dark:bg-dark-paper rounded-xl shadow-sm hover:shadow-md transition-shadow p-6"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <img
                        src={post.user.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(post.user.full_name)}`}
                        alt={post.user.full_name}
                        className="w-10 h-10 rounded-full"
                      />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{post.user.full_name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(post.created_at).toLocaleDateString([], {
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>

                    <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{post.content}</p>

                    {post.media_url && (
                      post.media_type === 'video' ? (
                        <video
                          src={post.media_url}
                          controls
                          className="mt-4 rounded-lg w-full"
                        />
                      ) : (
                        <img
                          src={post.media_url}
                          alt="Post media"
                          className="mt-4 rounded-lg w-full"
                        />
                      )
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                No posts yet
              </div>
            )}
          </div>
        )}

        {activeTab === 'members' && (
          <div className="space-y-8">
            {Object.entries(membersByRole).map(([role, members]) => (
              <div key={role}>
                <h3 className="text-lg font-semibold mb-4 capitalize text-gray-900 dark:text-gray-100">
                  {role}s ({members.length})
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {members.map((member) => (
                    <div
                      key={member.user.id}
                      className="flex items-center gap-4 p-4 bg-white dark:bg-dark-paper rounded-xl shadow-sm"
                    >
                      <img
                        src={member.user.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(member.user.full_name)}`}
                        alt={member.user.full_name}
                        className="w-12 h-12 rounded-full"
                      />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{member.user.full_name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Joined {new Date(member.joined_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}